import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Lightbulb, CheckCircle2, XCircle, Undo2, RefreshCw, Calendar, ChevronDown } from "lucide-react";
import { fetchRecommendations, refreshRecommendations, updateRecommendation } from "@/lib/api";
import { toast } from "sonner";

const PRIORITY_CONFIG = {
  HIGH: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)' },
  MEDIUM: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.3)' },
  LOW: { color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.3)' },
};

const TYPE_LABELS = {
  trending_opportunity: 'Trending', competitive_response: 'Competitive',
  reputation_management: 'Reputation', content_gap: 'Content Gap',
};

export default function RecommendationsPage() {
  const navigate = useNavigate();
  const [recs, setRecs] = useState([]);
  const [stats, setStats] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [filter, setFilter] = useState('active');
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      else params.include_resolved = true;
      if (selectedDate) params.date = selectedDate;
      const data = await fetchRecommendations(params);
      setRecs(data.recommendations || []);
      setStats(data.stats || null);
      if (data.available_dates) setAvailableDates(data.available_dates);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter, selectedDate]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshRecommendations();
      toast.success("Generating daily recommendations...");
      setTimeout(load, 8000);
    } catch (e) { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };

  const handleAction = async (recId, action) => {
    try {
      await updateRecommendation(recId, action);
      load();
    } catch (e) { toast.error("Action failed"); }
  };

  const TABS = [
    { key: 'active', label: 'Active', count: stats?.active },
    { key: 'actioned', label: 'Actioned', count: stats?.actioned },
    { key: 'dismissed', label: 'Dismissed', count: stats?.dismissed },
    { key: 'all', label: 'All', count: stats?.total },
  ];

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-800 transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Lightbulb className="w-5 h-5 text-yellow-400" />
          <h1 className="text-xl font-semibold text-zinc-100">AI Recommendations</h1>
        </div>
        <button onClick={handleRefresh} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/20 transition-colors rounded-lg"
          data-testid="refresh-recommendations">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Generate Now
        </button>
      </div>

      {/* Filter tabs + Date picker */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-white p-1 rounded-lg border border-gray-200">
          {TABS.map(tab => (
            <button key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === tab.key ? 'bg-zinc-700 text-zinc-100' : 'text-gray-500 hover:text-gray-800'
              }`}
              data-testid={`filter-${tab.key}`}>
              {tab.label} {tab.count != null && <span className="ml-1 text-gray-400">({tab.count})</span>}
            </button>
          ))}
        </div>

        {/* Date picker */}
        <div className="relative">
          <select
            value={selectedDate || ''}
            onChange={e => setSelectedDate(e.target.value || null)}
            className="appearance-none bg-gray-100 border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-xs text-gray-800 focus:outline-none focus:border-yellow-400/50 cursor-pointer"
            data-testid="date-picker"
          >
            <option value="">All dates</option>
            {availableDates.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <Calendar className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {/* Recommendations list */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">Loading...</div>
      ) : recs.length === 0 ? (
        <div className="text-center text-gray-500 py-12">No recommendations found for this filter.</div>
      ) : (
        <div className="space-y-3">
          {recs.map((rec, i) => {
            const p = PRIORITY_CONFIG[rec.priority] || PRIORITY_CONFIG.LOW;
            const isResolved = rec.status !== 'active';
            return (
              <Card key={rec.rec_id || i} className={`bg-gray-100 border-gray-200 ${isResolved ? 'opacity-60' : ''}`} data-testid={`rec-card-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge variant="outline" style={{ borderColor: p.border, color: p.color, background: p.bg }} className="text-[10px] font-bold">
                          {rec.priority}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-600">
                          {TYPE_LABELS[rec.type] || rec.type}
                        </Badge>
                        {rec.category && (
                          <Badge variant="outline" className="text-[10px] border-gray-200 text-gray-500">
                            {rec.category}
                          </Badge>
                        )}
                        {rec.generated_date && (
                          <span className="text-[10px] text-gray-400">{rec.generated_date}</span>
                        )}
                        {rec.status === 'actioned' && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Done</Badge>}
                        {rec.status === 'dismissed' && <Badge className="text-[10px] bg-zinc-600/20 text-gray-500 border-gray-300/30">Dismissed</Badge>}
                      </div>
                      <p className="text-sm font-medium text-gray-800">{rec.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{rec.description}</p>
                      {rec.data_signal && (
                        <p className="text-[10px] text-gray-400 mt-1 italic">Signal: {rec.data_signal}</p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {rec.status === 'active' ? (
                        <>
                          <button onClick={() => handleAction(rec.rec_id, 'done')}
                            className="p-1.5 text-emerald-500 hover:bg-emerald-500/10 rounded transition-colors" title="Mark as Done"
                            data-testid={`rec-done-${i}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleAction(rec.rec_id, 'dismiss')}
                            className="p-1.5 text-gray-500 hover:bg-zinc-500/10 rounded transition-colors" title="Dismiss"
                            data-testid={`rec-dismiss-${i}`}>
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => handleAction(rec.rec_id, 'restore')}
                          className="p-1.5 text-gray-500 hover:bg-zinc-500/10 rounded transition-colors" title="Restore"
                          data-testid={`rec-restore-${i}`}>
                          <Undo2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
